import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { HoldingClassRealisations } from '../EnterConsideration/ManualConsiderationForm/types';
import { round } from 'lodash';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material';

const InlineContainer = styled('div')({
  width: 'fit-content',
});

type Props = {
  currency: CurrencyCode;
  holdingClassRealisations: HoldingClassRealisations;
};

type Row = {
  id: string;
  label: string;
  valuesByHoldingClass: Record<string, number>;
};

const CashConsiderationsTable: React.FC<Props> = ({
  currency,
  holdingClassRealisations,
}) => {
  const rows: Array<Row> = [
    {
      id: 'received',
      label: 'Cash received value',
      valuesByHoldingClass: Object.fromEntries(
        holdingClassRealisations.map((hc) => [
          hc.holdingClassId,
          hc.cashConsiderationAmounts?.receivedConsiderationTotal || 0,
        ]),
      ),
    },
    {
      id: 'escrow',
      label: 'Cash escrow value',
      valuesByHoldingClass: Object.fromEntries(
        holdingClassRealisations.map((hc) => [
          hc.holdingClassId,
          hc.cashConsiderationAmounts?.escrowConsiderationTotal || 0,
        ]),
      ),
    },
    {
      id: 'earnout',
      label: 'Cash earnout value',
      valuesByHoldingClass: Object.fromEntries(
        holdingClassRealisations.map((hc) => [
          hc.holdingClassId,
          hc.cashConsiderationAmounts?.earnoutConsiderationTotal || 0,
        ]),
      ),
    },
    {
      id: 'withholding',
      label: 'Withholding tax payable',
      valuesByHoldingClass: Object.fromEntries(
        holdingClassRealisations.map((hc) => [
          hc.holdingClassId,
          hc.cashConsiderationAmounts?.withholdingTaxPayable || 0,
        ]),
      ),
    },
  ];

  const columns: Array<TableColumn<Row>> = [
    {
      key: 'label',
      render: ({ label }) => label,
      width: 250,
      footer: 'Total cash consideration value',
    },
    ...holdingClassRealisations.map((hc) => ({
      key: hc.holdingClassId,
      label: hc.name,
      render: (row: Row) =>
        numberToCurrencyString(
          row.valuesByHoldingClass[hc.holdingClassId] || 0,
          { currency, unlimitedDp: true },
        ),
      footer: numberToCurrencyString(
        round(
          (hc.cashConsiderationAmounts?.receivedConsiderationTotal || 0) +
            (hc.cashConsiderationAmounts?.escrowConsiderationTotal || 0) +
            (hc.cashConsiderationAmounts?.earnoutConsiderationTotal || 0) +
            (hc.cashConsiderationAmounts?.withholdingTaxPayable || 0),
          3,
        ),
        { currency, unlimitedDp: true },
      ),
    })),
  ];

  return (
    <InlineContainer>
      <Table columns={columns} rows={rows} />
    </InlineContainer>
  );
};

export default CashConsiderationsTable;
